/**
 * apIdentityState — what the screen says about WHO this login is, and why the
 * app looks the way it does.
 *
 * Why this module exists
 * ----------------------
 * On 2026-09-02 the AP app was blank on every list, with no error anywhere. The
 * sidebar read `ZZTest_AP_Andy` — the Entra guest display name — while the code
 * had in fact selected `ZA-001`. The wrong name on the screen made a
 * wrong-identity diagnosis look CONFIRMED when it was not, and cost an evening.
 * (ISS-032.)
 *
 * Two separate facts were being read as one:
 *
 *   the Entra guest that SIGNED IN   ≠   the AP_Master record that RESOLVED
 *
 * `describeAPShellIdentity` keeps them apart and labels whichever one is on
 * screen. `describeAPIdentityState` turns the four resolution outcomes (plus
 * ambiguity, which is a fifth treatment rather than a fifth outcome) into the
 * words a person reads.
 *
 * Why it is a module and not JSX
 * ------------------------------
 * There is no component test harness in this repo — no jsdom, no
 * testing-library; vitest is the only test tooling. A rule left in JSX is a
 * rule nothing can execute. 34-05/06/07 each extracted a pure tested presenter
 * beside the component for exactly this reason, and this follows them: the
 * decision of WHICH state to show and WHAT it says lives here with its own
 * cases; the component merely renders it.
 *
 * The server says the same things
 * -------------------------------
 * `api/shared/ap.js` refuses with a named code and logs one PII-free
 * `[ap-identity]` line per non-clean resolution. The candidate descriptors this
 * module's callers render — `id · APCode · Status` — are the same ones that
 * line carries, so the screen and the log can be compared word for word.
 */

import type { APIdentityOutcome } from '../../services/currentAPResolution';

/**
 * How the app should behave in front of this state — NOT a colour.
 *
 * - `none`       — nothing to say. Render the app.
 * - `setup`      — onboarding-shaped: the record does not exist yet.
 * - `blocked`    — the record exists but cannot be used until Harmony acts.
 * - `archived`   — the record resolved and is not Active. The archived
 *                  experience renders; this states the status plainly on it.
 * - `diagnostic` — Harmony's problem, not the provider's. The app renders
 *                  normally and this is shown to an operator only.
 */
export type APIdentityTone = 'none' | 'setup' | 'blocked' | 'archived' | 'diagnostic';

export interface APIdentityStateInput {
  /** The resolved AP_Master APCode, or null when none resolved. */
  apCode?: string | null;
  /** The resolved row's Status, when it is known. */
  status?: string | null;
  /** More than one owned row shares the winning status band. */
  ambiguous?: boolean;
  /** The resolved AP_Master Title, when it is known. */
  apName?: string | null;
}

export interface APIdentityState {
  /** Notice heading. `''` when there is nothing to show. */
  heading: string;
  /** Notice body. `''` when there is nothing to show. */
  body: string;
  tone: APIdentityTone;
  /**
   * This state is for an OPERATOR, not the provider: show it only under
   * `isDebugEnabled()`. True for exactly one state — a resolved, working,
   * ambiguous login. An ambiguity is Harmony's to fix; putting it in front of a
   * provider who is working fine would be noise that teaches them to ignore
   * the screen.
   */
  showDebug: boolean;
}

// ---------------------------------------------------------------------------
// The copy. Exported so a test asserts the exact sentence, not a paraphrase —
// 35-04 checks these strings on the live site.
// ---------------------------------------------------------------------------

/**
 * `no-match` — unchanged from what providers have been reading since Phase 30.
 * It is good copy and it is already in circulation; the only thing wrong with
 * it was that it was shown for FOUR different reasons.
 */
export const NO_MATCH_HEADING = 'Almost there';
export const NO_MATCH_BODY =
  'We don’t have a provider record matching this sign-in yet. Send the ID above ' +
  'to your Care Manager and they’ll finish setting up your access.';

/**
 * `no-apcode` — the state that used to be INDISTINGUISHABLE from a working
 * app. A row matched, so nothing said "you are not set up"; but with no APCode
 * nothing can be scoped to it, so every list was empty. It names the fix,
 * because waiting will not produce one.
 */
export const NO_APCODE_HEADING = 'Your provider record needs a provider code';
export const NO_APCODE_BODY =
  'A provider record matches this sign-in, but it has no provider code — so ' +
  'nothing can be linked to it: no shifts, no clients, no documents. Ask your ' +
  'Care Manager to add a provider code (APCode) to your record.';

/**
 * `not-active` — rendered on the archived experience, which is unchanged. The
 * status is stated plainly because "your profile is currently inactive" in the
 * warm copy below it does not tell an operator WHICH record is inactive, and a
 * provider who was told they were reinstated deserves to see the literal value.
 */
export const NOT_ACTIVE_BODY =
  'Client, shift and document access is paused. Compliance self-service stays ' +
  'open, so you can keep your documents up to date.';

/** `active` + ambiguous — operator-only. Never shown to a provider. */
export const AMBIGUOUS_HEADING = 'More than one provider record matches this sign-in';

/**
 * Describe the identity state behind what is (or is not) on the screen.
 *
 * @param outcome the resolution outcome. `null`/`undefined` falls back to the
 *                `no-match` copy, which is what an unexplained `apId === null`
 *                has always shown — a missing outcome must never blank the
 *                message out.
 */
export function describeAPIdentityState(
  outcome: APIdentityOutcome | null | undefined,
  input: APIdentityStateInput = {},
): APIdentityState {
  const apCode = clean(input.apCode);
  const status = clean(input.status);

  switch (outcome) {
    case 'active':
      // Ambiguity RESOLVES; it never blocks. The app runs; only an operator
      // hears about it.
      if (input.ambiguous) {
        return {
          heading: AMBIGUOUS_HEADING,
          body:
            `The app resolved to ${apCode || 'one of them'} and is working normally. ` +
            'Harmony needs to retire the duplicate records so this answer cannot drift.',
          tone: 'diagnostic',
          showDebug: true,
        };
      }
      return { heading: '', body: '', tone: 'none', showDebug: false };

    case 'not-active':
      return {
        heading: status
          ? `This provider record is currently ${status}`
          : 'This provider record is not active',
        body: NOT_ACTIVE_BODY,
        tone: 'archived',
        showDebug: false,
      };

    case 'no-apcode':
      return {
        heading: NO_APCODE_HEADING,
        body: NO_APCODE_BODY,
        tone: 'blocked',
        showDebug: false,
      };

    case 'no-match':
    default:
      return {
        heading: NO_MATCH_HEADING,
        body: NO_MATCH_BODY,
        tone: 'setup',
        showDebug: false,
      };
  }
}

// ---------------------------------------------------------------------------
// The two facts, kept apart
// ---------------------------------------------------------------------------

export interface APShellIdentityInput {
  /** Resolved AP_Master Title. */
  apName?: string | null;
  /** Resolved AP_Master APCode. */
  apCode?: string | null;
  /** Entra guest display name. */
  signInName?: string | null;
  /** Entra sign-in address (`account.username`). */
  signInEmail?: string | null;
}

export interface APShellIdentity {
  /** What the shell shows. */
  displayName: string;
  /** What that name IS — never omitted, so the two facts stay distinguishable. */
  caption: string;
  /** True when `displayName` names the AP_Master record the app is operating on. */
  resolved: boolean;
}

/** The caption over a resolved AP_Master record. */
export const PROVIDER_RECORD_CAPTION = 'Provider record';
/** The caption over the Entra identity, shown when no record resolved. */
export const SIGN_IN_CAPTION = 'Signed in as';
/** Last-resort name when the login carries neither a display name nor an address. */
export const UNKNOWN_SIGN_IN = 'AP User';

/**
 * Decide what the AP shell puts next to "Sign out", and say which fact it is.
 *
 * Where an `AP_Master` row resolved this is the record's `Title · APCode` — the
 * fact the app is ACTUALLY operating on. Where nothing resolved it falls back
 * to the Entra identity, captioned as the sign-in. The caption is present in
 * both cases on purpose: an unlabelled name is what allowed a guest display
 * name to be read as a provider record for an entire evening.
 */
export function describeAPShellIdentity(input: APShellIdentityInput): APShellIdentity {
  const apName = clean(input.apName);
  const apCode = clean(input.apCode);

  if (apName && apCode) {
    return { displayName: `${apName} · ${apCode}`, caption: PROVIDER_RECORD_CAPTION, resolved: true };
  }
  // A row resolved but is missing one half of its own name. Still the record —
  // showing the half we have beats falling back to a different person's name.
  if (apName || apCode) {
    return { displayName: apName || apCode, caption: PROVIDER_RECORD_CAPTION, resolved: true };
  }

  return {
    displayName: clean(input.signInName) || clean(input.signInEmail) || UNKNOWN_SIGN_IN,
    caption: SIGN_IN_CAPTION,
    resolved: false,
  };
}

// ---------------------------------------------------------------------------
// Candidate rows — the PII-free descriptors, formatted for a screen
// ---------------------------------------------------------------------------

/**
 * `id · APCode · Status`, the same three fields (and the same order) the
 * `[ap-identity]` server log writes as `id:apCode:status`. Deliberately never
 * an email or a name: this is safe to put in front of an operator, and it is
 * comparable to the log line by eye.
 */
export function formatAPCandidate(candidate: {
  id: string;
  apCode: string;
  status: string;
}): string {
  return [candidate.id || '?', candidate.apCode || '(no code)', candidate.status || '(no status)'].join(
    ' · ',
  );
}

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}
